/**
 * Layer options, the second level of the Layers sheet — §6.4, board 10.
 *
 * On a desktop a layer's options are a `⋯` and a context menu, which is the
 * right shape for a pointer and the wrong one for a sheet: a menu that opens
 * at a touch coordinate arrives as a floating card over a panel that came up
 * from the bottom, which is two shapes behind one control. §6.4 says what to
 * do instead — **the row itself opens a level**, the sheet's body is replaced
 * and `‹ Layers` names where the back link goes.
 *
 * **Rename and Colour are edited here, not one level further down.** §6.4
 * lists them as rows with values, and a third sheet to type eight characters
 * into is three taps to do what one row can: Rename reveals a field, Colour
 * reveals the eight swatches. **Who sees it** is the exception and keeps its
 * own surface, because it is the §5.5 radio cards, a division checklist with
 * crew counts and a switch — a screen, not a value.
 *
 * Every row is 52px, the destructive one is last and red, and the footer is
 * **Done**, which closes the level rather than saving anything: like every
 * other map surface, a change here is already written (§12 rule 9).
 */
import { useState } from 'react'
import { LAYER_COLORS, LAYER_PALETTE, type LayerColor } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Switch } from '../../components/primitives.js'
import {
  IconChevronRight, IconCopy, IconEdit, IconImage, IconLayers, IconPin, IconTrash,
  IconUsersRoles,
} from '../../frame/icons.js'
import { duplicateLayer, type MapFeature, type MapLayer } from '../api.js'
import type { LayerPatch } from '../editor/layers-panel.js'
import { AudienceChip, type Division } from '../sharing/audience-chip.js'
import { shapeWords } from '../words.js'

/**
 * The palette's own names, so a row can read `Colour · Teal` (§6.4).
 *
 * Labels only — the *order* the swatches come in is `LAYER_COLORS`, so there
 * is one answer to "which eight and in what order" and this cannot drift
 * from the New layer dialog's row.
 */
export const COLOUR_WORDS: Record<LayerColor, string> = {
  blue: 'Blue',
  teal: 'Teal',
  indigo: 'Indigo',
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
  purple: 'Purple',
  slate: 'Slate',
}

export function LayerOptionsSheet({
  layer, shapes, others, divisions, canEdit, onChange, onShare, onMove, onDelete, onDuplicated,
}: {
  layer: MapLayer
  shapes: MapFeature[]
  /** Every other layer on this map — where shapes can be moved to. */
  others: MapLayer[]
  divisions: Division[]
  canEdit: boolean
  onChange: (patch: LayerPatch) => void
  onShare: () => void
  onMove: (toLayerId: string) => void
  onDelete: () => void
  onDuplicated: () => void
}) {
  const [editing, setEditing] = useState<'name' | 'colour' | 'move' | null>(null)
  const [name, setName] = useState(layer.name)
  const mine = shapes.filter((one) => one.layerId === layer.id)

  const commitName = () => {
    const next = name.trim()
    setEditing(null)
    if (next.length > 0 && next !== layer.name) onChange({ name: next })
    else setName(layer.name)
  }

  return (
    <div className="flex flex-col">
      <p className="m-0 px-4 pb-1 pt-0.5 text-sub text-text-muted">
        {shapeWords(mine.length)}
        {' · shared with '}
        <AudienceChip
          audience={layer.audience}
          divisionIds={layer.divisionIds}
          divisions={divisions}
          className="text-sub"
        />
      </p>

      <Row
        icon={<IconEdit size={15} />}
        label="Rename"
        value={editing === 'name' ? undefined : layer.name}
        disabled={!canEdit}
        onClick={() => setEditing(editing === 'name' ? null : 'name')}
      />
      {editing === 'name' && (
        <div className="px-4 pb-3">
          <input
            value={name}
            autoFocus
            aria-label="Layer name"
            onChange={(event) => setName(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => { if (event.key === 'Enter') commitName() }}
            className={cn(
              'h-11 w-full rounded-field border border-line bg-surface px-3 text-body',
              'outline-none focus:border-accent-line',
            )}
          />
        </div>
      )}

      <Row
        icon={<IconImage size={15} />}
        label="Colour"
        value={COLOUR_WORDS[layer.color]}
        dot={LAYER_PALETTE[layer.color]}
        disabled={!canEdit}
        onClick={() => setEditing(editing === 'colour' ? null : 'colour')}
      />
      {editing === 'colour' && (
        <div className="flex flex-wrap gap-2.5 px-4 pb-3">
          {LAYER_COLORS.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={COLOUR_WORDS[key]}
              aria-pressed={layer.color === key}
              onClick={() => { onChange({ color: key }); setEditing(null) }}
              /* 44px, §7's touch size — the swatch inside stays 24px, the
                 same drawing the New layer dialog's row uses. */
              className="flex h-11 w-11 items-center justify-center"
            >
              <span
                className="h-6 w-6 rounded-tile"
                style={{
                  background: LAYER_PALETTE[key],
                  /* An inline halo rather than Tailwind's offset ring: the
                     gap colour would need a token of its own, and the halo
                     is the swatch. The same two lines `new-layer.tsx` has. */
                  ...(layer.color === key
                    ? {
                      boxShadow: '0 0 0 2px rgb(var(--color-surface)),'
                        + ` 0 0 0 4px ${LAYER_PALETTE[key]}`,
                    }
                    : {}),
                }}
              />
            </button>
          ))}
        </div>
      )}

      <Row
        icon={<IconUsersRoles size={15} />}
        label="Who sees it"
        value={(
          <AudienceChip
            audience={layer.audience}
            divisionIds={layer.divisionIds}
            divisions={divisions}
            className="text-sub"
          />
        )}
        chevron
        disabled={!canEdit}
        onClick={onShare}
      />

      {/*
        * The **row** toggles, not just the switch. A `Switch` is 34 × 20 —
        * right beside a field label, and the only thing on this row a thumb
        * could aim at while every other row here is 52px of target. The
        * switch stays as what it is — the thing that says which way it is
        * set — drawn `readOnly`, so it is a `<span>` rather than a second
        * button inside this one.
        */}
      <button
        type="button"
        role="switch"
        aria-checked={layer.pinnedToTickets}
        disabled={!canEdit}
        onClick={() => onChange({ pinnedToTickets: !layer.pinnedToTickets })}
        className={cn(
          'flex min-h-[52px] items-center gap-3 border-b border-line-faint px-4 py-2',
          'text-left text-body disabled:opacity-50',
          canEdit && 'hover:bg-hover',
        )}
      >
        <span className="inline-flex shrink-0 text-text-faint"><IconPin size={15} /></span>
        <span className="min-w-0 flex-1">Pin to visits here</span>
        <Switch
          readOnly
          checked={layer.pinnedToTickets}
          disabled={!canEdit}
          hideLabel
          label="Pin to visits here"
          onChange={() => {}}
        />
      </button>

      <Row
        icon={<IconCopy size={15} />}
        label="Duplicate layer"
        disabled={!canEdit}
        onClick={() => { void duplicateLayer(layer.id).then(onDuplicated) }}
      />

      <Row
        icon={<IconLayers size={15} />}
        label="Move shapes to another layer…"
        /* Nothing to move, or nowhere to move it: the row is absent rather
           than greyed, because a control that can never do anything is one
           more thing to read past. */
        hidden={mine.length === 0 || others.length === 0}
        disabled={!canEdit}
        onClick={() => setEditing(editing === 'move' ? null : 'move')}
      />
      {editing === 'move' && (
        <div className="flex flex-col px-4 pb-3">
          {others.map((other) => (
            <button
              key={other.id}
              type="button"
              onClick={() => { onMove(other.id); setEditing(null) }}
              className={cn(
                'flex min-h-11 items-center gap-2.5 rounded-button px-2 text-left text-body',
                'hover:bg-hover',
              )}
            >
              <span
                aria-hidden
                className="h-3 w-3 shrink-0 rounded-tile-small"
                style={{ background: LAYER_PALETTE[other.color] }}
              />
              {other.name}
            </button>
          ))}
        </div>
      )}

      <Row
        icon={<IconTrash size={15} />}
        label="Delete layer…"
        tone="danger"
        disabled={!canEdit}
        onClick={onDelete}
      />
    </div>
  )
}

function Row({ icon, label, value, dot, chevron, tone, hidden, disabled, onClick }: {
  /** Board 10 gives every row a 15px muted glyph; the red one's is red. */
  icon?: React.ReactNode
  label: string
  value?: React.ReactNode
  dot?: string
  chevron?: boolean
  tone?: 'danger'
  hidden?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  if (hidden) return null
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-[52px] items-center gap-3 border-b border-line-faint px-4 py-2 text-left',
        'text-body disabled:opacity-50',
        tone === 'danger' ? 'text-danger' : 'text-text',
        !disabled && 'hover:bg-hover',
      )}
    >
      {icon && (
        <span className={cn(
          'inline-flex shrink-0',
          tone === 'danger' ? 'text-danger' : 'text-text-faint',
        )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">{label}</span>
      {dot && (
        <span
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 rounded-tile-small"
          style={{ background: dot }}
        />
      )}
      {value !== undefined && (
        <span className="min-w-0 shrink truncate text-sub text-text-muted">{value}</span>
      )}
      {chevron && (
        <span className="inline-flex shrink-0 text-text-faint"><IconChevronRight size={15} /></span>
      )}
    </button>
  )
}
